import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"
import { AdjustOnlyVisual } from "@/js/pages/product/rackbuilder/constants"
import type { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"
export { createTrolleyClip, createLatBarClip, createLowRowClip, createFootplate, buildTopWeightStackHardware, buildWeightStack300Plates } from "../ftSharedParts"
import { buildTopWeightStackHardware, buildWeightStack300Plates } from "../ftSharedParts"
import { getFlipX, getRotateAndFlipX } from "../../ftAdjustments"

// Twin Parts — All part definitions for FM-6-Twin and FM-HR-Twin

// MARK: Context
export interface TwinContext {
  config: RackOptions
  materials: MaterialInfo
  height: RackOptions['height']
  depth: string
  isTwin80: boolean
  isHalfRack: boolean
}

// MARK: Side Meshes
export function createSideMeshes(context: TwinContext, left: boolean): PartDefinition[] {
  const { config, materials, height, isTwin80, isHalfRack } = context
  const twinFile = isTwin80 ? 'FM_TWIN_80' : 'FM_TWIN'
  const sideMeshes: PartDefinition[] = []

  // Twin stack mesh
  const stackMesh: PartDefinition = {
    mesh        : getRackModel('FM_SHARED', 'weight stack'),
    materials,
    adjustments : getFlipX(left),
    slots       : {},
  }
  
  if (config.weightStackAmount === '300') {
    stackMesh.slots.weights_300 = [buildTopWeightStackHardware(), buildWeightStack300Plates()]
  } else {
    stackMesh.slots.weights_250 = buildTopWeightStackHardware()
  }

  // Add back cable for 80" full racks
  if (isTwin80 && !isHalfRack) {
    stackMesh.slots.cable = {
      mesh   : getRackModel('FM_TWIN_CABLES', 'back cable'),
      slots  : {},
      offset : { x: 0.2, y: 0.2 },
    }
  }
  
  sideMeshes.push(stackMesh)
  
  // Twin stack poles
  const stackPoles: PartDefinition = {
    mesh        : getRackModel(twinFile, `stack poles ${height}`),
    materials,
    anchor      : 'top',
    adjustments : getFlipX(left),
    slots       : {},
  }

  if (isTwin80) {
    const bottomCableMesh = isHalfRack
      ? getRackModel('FM_TWIN_CABLES', `weight stack pulley cable hr ${config.weightStackAmount} 80`)
      : getRackModel('FM_TWIN_CABLES', `weight stack pulley cable ${config.weightStackAmount} 80`)
    const cableOffset = isHalfRack ? { x: -0.1, z: 0.12 } : { x: 0.05, z: 0.02 }
    stackPoles.slots.bottom = { mesh: bottomCableMesh, slots: {}, offset: cableOffset }
  } else {
    stackPoles.slots.top = { mesh: getRackModel('FM_TWIN_CABLES', `top cable ${height}`), slots: {} }
    stackPoles.slots.bottom = { mesh: getRackModel('FM_TWIN_CABLES', `weight stack pulley cable ${config.weightStackAmount}`), slots: {} }
  }

  sideMeshes.push(stackPoles)
  
  if (config.ftShrouds) {
    sideMeshes.push({
      mesh        : getRackModel(twinFile, `shroud ${height}`),
      slots       : {},
      offset      : isTwin80 ? { z: 0.3 } : {},
      adjustments : [], // Twin shrouds are identical on both sides
    })
  }
  
  return sideMeshes
}

// MARK: Front Bottom Pulley
export function createFrontBottomPulley(
  context: TwinContext, 
  left: boolean,
  trolleyClip: PartDefinition,
  carriage: PartDefinition
): PartDefinition {
  const { materials, isTwin80, isHalfRack } = context
  
  if (isTwin80) {
    return createTwin80FrontBottomPulley(context, left, trolleyClip)
  }
  
  const mesh = isHalfRack
    ? getRackModel('FM_TWIN', 'halfrack bottom pulley')
    : getRackModel('FM_SHARED', 'bottom front pulley')

  return {
    mesh,
    adjustments : getFlipX(left),
    materials,
    slots       : { front: carriage },
  }
}

function createTwin80FrontBottomPulley(
  context: TwinContext,
  left: boolean,
  trolleyClip: PartDefinition
): PartDefinition {
  const { config, materials, depth, isHalfRack } = context

  // 80" specific cable
  const cable80 = isHalfRack
    ? { mesh: getRackModel('FM_TWIN_CABLES', 'twin cable hr 80'), slots: {}, materials, offset: { x: -0.1, z: -0.1 } }
    : { mesh: getRackModel('FM_TWIN_CABLES', 'twin front bottom cable 80'), slots: {}, materials }

  // 80" carriage branding plate — attaches into the cover slot on the carriage mesh
  const rogueLettering80: PartDefinition = {
    mesh        : getRackModel('FM_TWIN_80', 'rogue lettering 80'),
    slots       : {},
    adjustments : left ? [] : [{ type: 'rotate', amount: 180, axis: 'Y' }, { type: 'flip', axis: 'Z' }],
    offset      : left ? {} : { x: -0.38 },
  }
  
  const brandingPlate80: PartDefinition = {
    mesh  : getRackModel('FM_TWIN_80', 'carriage branding plate 80'),
    slots : {
      lettering: rogueLettering80,
    },
    anchor: 'cover',
    
    offset      : left ? {} : { z: 0.1, x: -0.3 },
    adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
  }

  const carriage80: PartDefinition = {
    mesh   : getRackModel('FM_TWIN_80', 'carriage 80'),
    materials,
    anchor : 'bottom',
    offset : isHalfRack ? { z: -1.5 } : { z: -0.2 },
    slots  : { cable: cable80, trolley: config.trolleySelection ? trolleyClip : [], cover: brandingPlate80 },
  }
  
  // Pulley hardware for full rack
  const pulleyHardware: PartDefinition = {
    mesh  : getRackModel('FM_TWIN_80', 'pulley hardware'),
    materials,
    slots : {},
  }

  const pulley: PartDefinition = {
    mesh        : isHalfRack ? getRackModel('FM_TWIN_80', 'halfrack bottom pulley 80') : getRackModel('FM_TWIN_80', 'bottom front pulley 80'),
    materials,
    adjustments : getFlipX(left),
    slots       : { front: carriage80 },
  }
  
  // Add hardware and offset for full rack
  if (!isHalfRack) {
    pulley.slots[`hardware_${depth}_pulley`] = pulleyHardware
    pulley.offset = { z: 6.2 }
  }
  
  return pulley
}

// MARK: Half Rack Top Pulley
export function createHalfRackTopPulley(context: TwinContext, left: boolean): PartDefinition {
  const { materials, isTwin80 } = context
  
  if (isTwin80) {
    return {
      mesh        : getRackModel('FM_TWIN_80', 'halfrack top pulley 80'),
      materials,
      slots       : {},
      offset      : left ? { x: 0.5 } : { x: -0.5 },
      adjustments : getFlipX(left),
    }
  }
  
  return {
    mesh        : getRackModel('FM_TWIN', 'halfrack top pulley'),
    materials,
    slots       : {},
    offset      : { x: 0, z: 0 },
    adjustments : getFlipX(left),
  }
}

// MARK: Back Bottom Pulley
export function createBackBottomPulley(context: TwinContext, left: boolean): PartDefinition {
  const { materials, isTwin80 } = context
  
  return {
    mesh        : getRackModel('FM_TWIN', 'bottom back pulley'),
    materials,
    slots       : {},
    adjustments : isTwin80 && !left
      ? getRotateAndFlipX(false)
      : getFlipX(left),
  }
}

// MARK: Rails System
export function createTopRailPulley(context: TwinContext, left: boolean): PartDefinition {
  const { materials, depth, isTwin80 } = context
  
  if (isTwin80) {
    return createTwin80TopRailPulley(context, left)
  }
  
  const topBackPulley: PartDefinition = {
    mesh  : getRackModel('FM_TWIN', 'top back pulley'),
    materials,
    slots : {},
  }

  const topRail: PartDefinition = {
    mesh           : getRackModel('FM_TWIN', `top rail ${depth}`),
    materials,
    slots          : { back: [topBackPulley] },
    adjustments    : getRotateAndFlipX(left),
    adjustmentType : left ? undefined : AdjustOnlyVisual,
  }

  return {
    mesh        : getRackModel('FM_SHARED', 'top front pulley'),
    materials,
    slots       : { rail: topRail },
    adjustments : getFlipX(left),
  }
}

function createTwin80TopRailPulley(context: TwinContext, left: boolean): PartDefinition {
  const { materials, depth } = context
  
  const topBackPulley: PartDefinition = {
    mesh        : getRackModel('FM_TWIN', 'top back pulley'),
    materials,
    slots       : {},
    adjustments : getRotateAndFlipX(left),
  }
  
  const pulleyHardware: PartDefinition = {
    mesh  : getRackModel('FM_TWIN_80', 'pulley hardware'),
    materials,
    slots : {},
  }

  const topRail80: PartDefinition = {
    mesh        : getRackModel('FM_TWIN_80', `top rail ${depth} 80`),
    materials,
    anchor      : !left ? 'right' : '',
    slots       : left ? { back: [topBackPulley] } : { front: [topBackPulley] },
    adjustments : getRotateAndFlipX(left),
  }

  return {
    mesh        : getRackModel('FM_TWIN_80', 'top front pulley 80'),
    materials,
    slots       : { rail: topRail80, [`hardware_${depth}_pulley`]: pulleyHardware },
    offset      : left ? { x: 0.5, z: -6.1 } : { x: -0.5, z: -6.1 },
    adjustments : getFlipX(left),
  }
}

export function createBottomRail(context: TwinContext, left: boolean): PartDefinition {
  const { materials, depth, isTwin80 } = context
  
  if (isTwin80) {
    return {
      mesh        : getRackModel('FM_TWIN_80', `bottom rail ${depth} 80`),
      materials,
      slots       : {},
      anchor      : left ? 'main' : 'right',
      offset      : left ? {} : { x: -0.05 },
      adjustments : getRotateAndFlipX(left),
    }
  }
  
  return {
    mesh           : getRackModel('FM_TWIN', `bottom rail ${depth}`),
    materials,
    slots          : {},
    anchor         : 'main',
    adjustments    : getRotateAndFlipX(left),
    adjustmentType : AdjustOnlyVisual,
  }
}

// MARK: Resistance System Parts
export function createBackPost(context: TwinContext, latBarClip: PartDefinition): PartDefinition {
  const { materials, height, isTwin80 } = context
  
  const latPulldown: PartDefinition = {
    mesh  : isTwin80 ? getRackModel('FM_TWIN_80', 'lat pulldown 80') : getRackModel('FM_SHARED', 'lat pulldown'),
    materials,
    slots : isTwin80 ? { lat: latBarClip } : { attachment_clip: latBarClip },
  }
  
  return {
    mesh  : getRackModel(isTwin80 ? 'FM_TWIN_80' : 'FM_TWIN', `back post ${height}`),
    materials,
    slots : {
      top: {
        mesh  : getRackModel('FM_TWIN', 'back post top'),
        materials,
        slots : { front: latPulldown },
      },
    },
  }
}

export function createBackShroud(context: TwinContext): PartDefinition {
  const { materials, height, isTwin80 } = context

  return {
    mesh  : getRackModel(isTwin80 ? 'FM_TWIN_80' : 'FM_TWIN', `back shroud ${height}`),
    materials,
    slots : {},
  }
}

export function createRearCrossmember(
  context: TwinContext, 
  footplate: PartDefinition, 
  resistanceMeshes: PartDefinition[]
): PartDefinition {
  const { materials } = context
  
  return {
    mesh  : getRackModel('FM_SHARED', 'rear crossmember'),
    materials,
    slots : { footplate, back: resistanceMeshes },
  }
}

